import React, { FC, MouseEvent, ReactElement, useEffect, useState } from "react";
import {
  UserCompanyRoleAssignmentDto,
  UserRole,
  UsersListItemDto,
} from "@green0meter/api-client";
import { useTranslation } from "react-i18next";
import FeatherIcon from "feather-icons-react";
import { groupBy, isEmpty } from "lodash";
import { TFunction } from "i18next";

import { COMPANY_FILTER_DEFAULT_VALUE } from "../TableToolbar/TableToolbar";

import Table, { TableColumn } from "@components/common/Table";
import { DEFAULT_PAGE_SIZE } from "@constants";
import { Chip } from "@components/common";
import { USER_ROLE_VALUES } from "@type/User";
import { useGetUsers, useGetCompaniesBasics } from "@api";
import { Stack, IconButton } from "@components/base";
import { useUserStore } from "@store/user";

interface UsersTable {
  onUserEdit: (user: UsersListItemDto) => void;
  onBlockUser: (user: UsersListItemDto) => void;
  companyNameFilter: string;
  companyIdFilter: string;
}

const UsersTable: FC<UsersTable> = ({
  onUserEdit,
  onBlockUser,
  companyIdFilter,
  companyNameFilter,
}) => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [pageNumber, setPageNumber] = useState(1);
  const { companiesBasics, refetchCompaniesBasics } = useGetCompaniesBasics(user);
  const { users, refetchUsers } = useGetUsers(user, {
    companyId:
      companyIdFilter === COMPANY_FILTER_DEFAULT_VALUE ? undefined : companyIdFilter,
    name: companyNameFilter,
    pageSize,
    pageNumber,
  });

  const handleUserRemove = (
    e: MouseEvent<HTMLButtonElement>,
    user: UsersListItemDto,
  ): void => {
    e.preventDefault();
    e.stopPropagation();

    onBlockUser(user);
  };

  const getCompanyAssignmentLabels = (
    t: TFunction<"translation", undefined>,
    companies?: UserCompanyRoleAssignmentDto[],
  ): ReactElement => {
    const assignedCompanies =
      companies?.filter(
        (company): company is UserCompanyRoleAssignmentDto & { role: UserRole } =>
          company.isAssigned && company.role !== null,
      ) || [];

    if (isEmpty(assignedCompanies)) {
      return (
        <Chip
          label={t("usersManagement.table.noEntities")}
          type="BusinessAssociation"
          size="small"
        />
      );
    }

    if (!companiesBasics || isEmpty(companiesBasics?.items)) {
      const groupedCompanies = groupBy(assignedCompanies, "role");
      const groupedCompaniesKeys = Object.keys(groupedCompanies);

      return (
        <Stack direction="row" spacing={1}>
          <Chip
            label={t("usersManagement.table.entityAssignment", {
              count: assignedCompanies.length,
            })}
            type="BusinessAssociation"
            size="small"
          />
          {groupedCompaniesKeys.map((key) => {
            const groupedCompany = groupedCompanies[key];

            return (
              <Chip
                key={key}
                label={t(`roles.${USER_ROLE_VALUES[groupedCompany[0].role]}`)}
                type={groupedCompany[0].role}
                size="small"
              />
            );
          })}
        </Stack>
      );
    }

    type BasicsItem = {
      id: string;
      name: string;
      companyType: number;
    };

    const basicsItems = companiesBasics.items as BasicsItem[];

    const activeCompany = basicsItems.find(
      (company) => company.id === user?.activeCompanyId,
    );

    const isMasterContext = activeCompany?.companyType === 0;

    const relevantBasics = isMasterContext
      ? basicsItems
      : basicsItems.filter((company) => company.companyType !== 0);

    const relevantCompanies = assignedCompanies.filter((company) =>
      relevantBasics.some((basic) => basic.id === company.companyId),
    );

    if (relevantCompanies.length === 0) {
      return (
        <Chip
          label={t("usersManagement.table.entityAssignment", {
            count: 0,
          })}
          type="BusinessAssociation"
          size="small"
        />
      );
    }

    const groupedCompanies = groupBy(relevantCompanies, "role");
    const groupedCompaniesKeys = Object.keys(groupedCompanies);

    return (
      <Stack direction="row" spacing={1}>
        <Chip
          label={t("usersManagement.table.entityAssignment", {
            count: relevantCompanies.length,
          })}
          type="BusinessAssociation"
          size="small"
        />
        {groupedCompaniesKeys.map((key) => {
          const groupedCompany = groupedCompanies[key];

          return (
            <Chip
              key={key}
              label={t(`roles.${USER_ROLE_VALUES[groupedCompany[0].role]}`)}
              type={groupedCompany[0].role}
              size="small"
            />
          );
        })}
      </Stack>
    );
  };

  const columns: TableColumn<UsersListItemDto>[] = [
    {
      title: t("usersManagement.table.name"),
      key: "name",
      renderCellContent: (user) => `${user.firstName} ${user.lastName}`,
    },
    {
      title: t("usersManagement.table.email"),
      key: "email",
      dataKey: "email",
    },
    {
      title: t("usersManagement.table.phone"),
      key: "phoneNumber",
      dataKey: "phoneNumber",
    },
    {
      key: "role",
      title: t("usersManagement.table.entityAssignmentColumn"),
      renderCellContent: (user) => getCompanyAssignmentLabels(t, user.companies),
    },
    {
      title: t("usersManagement.table.actions"),
      key: "actions",
      renderCellContent: (rowUser) => (
        <>
          <IconButton onClick={() => onUserEdit(rowUser)} color="primary">
            <FeatherIcon icon="edit-3" size={18} />
          </IconButton>
          <IconButton
            onClick={(e) => handleUserRemove(e, rowUser)}
            color="error"
            disabled={rowUser.id === user?.id}
          >
            <FeatherIcon icon="trash-2" size={18} />
          </IconButton>
        </>
      ),
      tableCellProps: {
        align: "center",
        width: 120,
      },
    },
  ];

  useEffect(() => {
    refetchUsers();
    refetchCompaniesBasics();
  }, []);

  return (
    <>
      <Table<UsersListItemDto>
        data={users?.items ?? []}
        onRowClick={(user) => onUserEdit(user)}
        columns={columns}
        pagination={{
          onPageNumberChange: (pageNumber) => setPageNumber(pageNumber),
          onPageSizeChange: (pageSize) => setPageSize(pageSize),
          pageNumber,
          totalItems: users?.totalCount,
          pageCount: users?.totalPages,
          pageSize,
        }}
        tableProps={{
          size: "small",
        }}
      />
    </>
  );
};

export default UsersTable;
